import { useEffect, useRef, useState } from 'react';

/**
 * Detects a part that has JUST become unlocked, so the member gets the
 * celebratory unlock beat (design G2) exactly once.
 *
 * Why this is needed: a member unlocks a chapter by leaving feedback in the
 * READER, then navigates back here — so the transition never happens while this
 * screen is mounted and can't be observed from a mutation. We instead remember
 * the parts we've already acknowledged, per group, and treat anything new in
 * `unlockedParts` as freshly unlocked.
 *
 * `sessionStorage` (not local) is deliberate: the beat is a nice-to-have, and a
 * new session re-celebrating once is far better than persisting cross-device
 * state or nagging forever. All access is wrapped — Safari private mode throws
 * on sessionStorage access, and a decorative animation must never break the
 * feedback view.
 */

const key = (groupId: string) => `critique-unlocked-seen:${groupId}`;

const readSeen = (groupId: string): string[] => {
  try {
    const raw = window.sessionStorage.getItem(key(groupId));
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const writeSeen = (groupId: string, parts: string[]): void => {
  try {
    window.sessionStorage.setItem(key(groupId), JSON.stringify(parts));
  } catch {
    // Storage unavailable (private mode / quota) — skip the beat, never throw.
  }
};

export const useNewlyUnlockedPart = (
  groupId: string | undefined,
  unlockedParts: string[] | undefined
): { newlyUnlocked: string | undefined; acknowledge: () => void } => {
  const [newlyUnlocked, setNewlyUnlocked] = useState<string | undefined>();
  // First render must not celebrate every part a returning member already had.
  const primed = useRef(false);

  useEffect(() => {
    // `unlockedParts` is absent when no gate applies (host view / immediate) —
    // there is nothing to unlock, so never celebrate.
    if (!groupId || !unlockedParts) return;

    const seen = readSeen(groupId);
    const fresh = unlockedParts.filter(p => !seen.includes(p));

    if (!primed.current && seen.length === 0) {
      // First ever visit: record what they already have, celebrate nothing.
      primed.current = true;
      writeSeen(groupId, unlockedParts);
      return;
    }
    primed.current = true;

    if (fresh.length > 0) {
      setNewlyUnlocked(fresh[fresh.length - 1]);
      writeSeen(groupId, unlockedParts);
    }
  }, [groupId, unlockedParts?.join(',')]);

  return {
    newlyUnlocked,
    acknowledge: () => setNewlyUnlocked(undefined),
  };
};

export default useNewlyUnlockedPart;
